import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import {
  Loader2, Shield, ShieldOff, Copy, Check, Download,
  ArrowRight, ArrowLeft, Smartphone, Key, Lock, CheckCircle,
} from 'lucide-react';

interface TwoFactorSetupProps {
  enabled: boolean;
  onStatusChange: (enabled: boolean) => void;
}

// ── Setup wizard steps ───────────────────────────────────────────
// intro → setup → verify → codes → complete
type WizardStep = 'intro' | 'setup' | 'verify' | 'codes' | 'complete';

const TOTAL_STEPS = 5; // intro=1, setup=2, verify=3, codes=4, complete=5

export function TwoFactorSetup({ enabled, onStatusChange }: TwoFactorSetupProps) {
  // ── Wizard state ────────────────────────────────────────────────
  const [wizardStep, setWizardStep] = useState<WizardStep>('intro');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [codesSaved, setCodesSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [viewOnlyCodes, setViewOnlyCodes] = useState(false); // true when viewing codes from already-enabled state

  // ── Disable state ───────────────────────────────────────────────
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  // ── Step 1: Generate secret + QR ─────────────────────────────────
  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post<any>('/auth/2fa/setup');
      if (res.success && res.data) {
        setQrCode(res.data.qrCode);
        setSecret(res.data.secret);
        setBackupCodes(res.data.backupCodes);
        setWizardStep('setup');
      }
    } catch (err: any) {
      toast({
        title: 'Setup failed',
        description: err?.message || 'Failed to initialize 2FA setup.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 → 3: After scanning QR, go to verify ──────────────────
  const handleGoToVerify = () => {
    setWizardStep('verify');
  };

  // ── Step 3: Verify TOTP code ─────────────────────────────────────
  const handleVerify = async () => {
    if (!verifyCode.trim()) return;
    setVerifying(true);
    try {
      const res = await apiClient.post('/auth/2fa/verify-setup', { code: verifyCode.trim() });
      if (res.success) {
        setWizardStep('codes');
      }
    } catch (err: any) {
      toast({
        title: 'Verification failed',
        description: err?.message || 'Invalid code. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  // ── Step 4: Complete (codes saved) ───────────────────────────────
  const handleComplete = () => {
    if (!codesSaved) return;
    onStatusChange(true);
    setWizardStep('complete');
    toast({
      title: '2FA enabled',
      description: 'Two-factor authentication has been activated.',
      variant: 'success',
    });
  };

  // ── Copy / Download codes ────────────────────────────────────────
  const handleCopyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCodes = () => {
    const content = `DynDev PM — Backup Recovery Codes\nGenerated: ${new Date().toISOString().split('T')[0]}\n\n${backupCodes.join('\n')}\n\nKeep these codes safe. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dyndev-2fa-backup-codes.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Disable 2FA ─────────────────────────────────────────────────
  const handleDisable = async () => {
    if (!disablePassword) return;
    setLoading(true);
    try {
      const res = await apiClient.post('/auth/2fa/disable', { password: disablePassword });
      if (res.success) {
        onStatusChange(false);
        setWizardStep('intro');
        setShowDisable(false);
        setDisablePassword('');
        toast({
          title: '2FA disabled',
          description: 'Two-factor authentication has been turned off.',
          variant: 'success',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Failed to disable',
        description: err?.message || 'Incorrect password.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── View recovery codes (already enabled) ────────────────────────
  const handleViewRecoveryCodes = async () => {
    try {
      const res = await apiClient.post<any>('/auth/2fa/recovery-codes');
      if (res.success && res.data) {
        setBackupCodes(res.data.backupCodes);
        setViewOnlyCodes(true);
      }
    } catch (err: any) {
      toast({ title: 'Failed to generate codes', description: err?.message, variant: 'destructive' });
    }
  };

  // ── Inline codes view (for already-enabled users viewing codes) ──
  if (viewOnlyCodes) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Recovery Codes</CardTitle>
          </div>
          <CardDescription>
            Save these one-time use codes somewhere safe. If you lose your authenticator app, you can use a backup code to sign in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-[var(--bg-secondary)] rounded-lg p-3 font-mono text-xs space-y-1">
            {backupCodes.map((code, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-dyndev-green font-bold">{String(i + 1).padStart(2, '0')}.</span>
                <span>{code}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleCopyCodes} className="gap-1.5 text-xs">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy Codes'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownloadCodes} className="gap-1.5 text-xs">
              <Download size={12} />
              Download
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={() => setViewOnlyCodes(false)} className="w-full sm:w-auto">
            Done
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Step indicator component ─────────────────────────────────────
  const StepIndicator = ({ current }: { current: number }) => (
    <div className="flex items-center justify-center gap-1.5 mb-5">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current
              ? 'w-6 bg-dyndev-green'
              : i === current
              ? 'w-6 bg-dyndev-green'
              : 'w-1.5 bg-[var(--border-color)]'
          }`}
        />
      ))}
    </div>
  );

  // ── Back button helper ───────────────────────────────────────────
  const BackButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outline" size="sm" onClick={onClick} className="gap-1">
      <ArrowLeft size={14} />
      Back
    </Button>
  );

  // =================================================================
  // WIZARD STEPS
  // =================================================================

  // ── Step 1: Introduction ───────────────────────────────────────
  if (wizardStep === 'intro' && !enabled) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Add an extra layer of security to your account by requiring a one-time verification code from your phone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
              <Smartphone size={20} className="text-dyndev-green mx-auto mb-1.5" />
              <p className="text-xs font-medium">Authenticator App</p>
              <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                Google Authenticator, Authy, or 1Password
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
              <Key size={20} className="text-dyndev-green mx-auto mb-1.5" />
              <p className="text-xs font-medium">Backup Codes</p>
              <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                8 one-time use codes for emergency access
              </p>
            </div>
            <div className="p-3 rounded-lg bg-[var(--bg-secondary)] text-center">
              <Lock size={20} className="text-dyndev-green mx-auto mb-1.5" />
              <p className="text-xs font-medium">Stronger Security</p>
              <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                Protects your account even if your password is compromised
              </p>
            </div>
          </div>

          <Button size="sm" disabled={loading} onClick={handleStartSetup} className="gap-1.5 w-full sm:w-auto">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {loading ? 'Setting up...' : (
              <>
                Get Started
                <ArrowRight size={14} />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Step 2: Scan QR Code ───────────────────────────────────────
  if (wizardStep === 'setup') {
    const stepNumber = 2;
    return (
      <Card className="border-dyndev-green/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Set Up Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Scan the QR code with your authenticator app (e.g. Google Authenticator, Authy).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StepIndicator current={stepNumber - 1} />

          {/* QR Code */}
          <div className="flex justify-center">
            <div className="p-4 rounded-xl bg-white border border-[var(--border-color)]">
              <img src={qrCode} alt="2FA QR Code" className="w-52 h-52" />
            </div>
          </div>

          {/* Manual entry key */}
          <div className="text-center">
            <p className="text-xs text-[var(--text-secondary)] mb-1">
              Can't scan the code? Enter this key manually:
            </p>
            <code className="text-xs bg-[var(--bg-secondary)] px-2 py-1 rounded font-mono select-all break-all">
              {secret}
            </code>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <BackButton onClick={() => setWizardStep('intro')} />
            <Button size="sm" onClick={handleGoToVerify} className="gap-1.5">
              Next
              <ArrowRight size={14} />
            </Button>
          </div>

          <p className="text-[10px] text-[var(--text-secondary)] text-center">
            Step {stepNumber} of {TOTAL_STEPS}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Step 3: Verify Code ────────────────────────────────────────
  if (wizardStep === 'verify') {
    const stepNumber = 3;
    return (
      <Card className="border-dyndev-green/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Verify Setup</CardTitle>
          </div>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to confirm setup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StepIndicator current={stepNumber - 1} />

          {/* Verify input */}
          <div className="space-y-2">
            <Label htmlFor="verify-2fa-code">Verification Code</Label>
            <div className="flex items-center gap-2">
              <Input
                id="verify-2fa-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="font-mono text-center text-lg tracking-widest max-w-[160px]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && verifyCode.length === 6 && !verifying) {
                    handleVerify();
                  }
                }}
              />
              <Button
                size="sm"
                disabled={verifying || verifyCode.length !== 6}
                onClick={handleVerify}
                className="gap-1.5"
              >
                {verifying && <Loader2 size={12} className="animate-spin" />}
                {verifying ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <BackButton onClick={() => setWizardStep('setup')} />
            <div /> {/* spacer for flex alignment */}
          </div>

          <p className="text-[10px] text-[var(--text-secondary)] text-center">
            Step {stepNumber} of {TOTAL_STEPS}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Step 4: Backup Codes ───────────────────────────────────────
  if (wizardStep === 'codes') {
    const stepNumber = 4;
    return (
      <Card className="border-dyndev-green/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Save Your Backup Codes</CardTitle>
          </div>
          <CardDescription>
            Save these one-time use codes somewhere safe. If you lose your authenticator app, you can use a backup code to sign in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StepIndicator current={stepNumber - 1} />

          {/* Code list */}
          <div className="bg-[var(--bg-secondary)] rounded-lg p-3 font-mono text-xs space-y-1">
            {backupCodes.map((code, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-dyndev-green font-bold">{String(i + 1).padStart(2, '0')}.</span>
                <span>{code}</span>
              </div>
            ))}
          </div>

          {/* Copy / Download */}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleCopyCodes} className="gap-1.5 text-xs">
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy Codes'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownloadCodes} className="gap-1.5 text-xs">
              <Download size={12} />
              Download
            </Button>
          </div>

          {/* Must-confirm checkbox */}
          <label
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => setCodesSaved(!codesSaved)}
          >
            <div
              className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                codesSaved
                  ? 'bg-dyndev-green border-dyndev-green'
                  : 'border-[var(--border-color)] group-hover:border-dyndev-green/50'
              }`}
            >
              {codesSaved && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="text-xs text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors select-none">
              I have saved my backup codes in a safe place
            </span>
          </label>

          {/* Complete button */}
          <Button
            size="sm"
            disabled={!codesSaved}
            onClick={handleComplete}
            className="gap-1.5 w-full sm:w-auto"
          >
            <CheckCircle size={14} />
            Complete Setup
          </Button>

          <p className="text-[10px] text-[var(--text-secondary)] text-center">
            Step {stepNumber} of {TOTAL_STEPS}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Step 5: Confirmation ──────────────────────────────────────
  if (wizardStep === 'complete') {
    return (
      <Card className="border-dyndev-green/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Two-Factor Authentication Enabled</CardTitle>
          </div>
          <CardDescription>
            Your account is now protected with two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 rounded-lg bg-dyndev-green/5 border border-dyndev-green/20">
            <p className="text-xs font-medium text-dyndev-green mb-1">✅ Two-factor authentication is active</p>
            <p className="text-[10px] text-[var(--text-secondary)]">
              You'll need a verification code from your authenticator app each time you sign in. Keep your backup codes safe — if you lose access, you'll need them to recover your account.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleViewRecoveryCodes} className="gap-1.5">
              View Recovery Codes
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDisable(true)}
              className="gap-1.5 text-red-400 border-red-500/20 hover:bg-red-500/10 hover:text-red-300"
            >
              <ShieldOff size={12} />
              Disable 2FA
            </Button>
          </div>

          {showDisable && (
            <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
              <p className="text-xs text-[var(--text-secondary)]">
                Enter your password to disable two-factor authentication.
              </p>
              <Input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                className="text-sm h-8 max-w-xs"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={loading || !disablePassword}
                  onClick={handleDisable}
                  className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
                >
                  {loading && <Loader2 size={12} className="animate-spin" />}
                  Confirm Disable
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // =================================================================
  // ALREADY ENABLED — NOT IN WIZARD
  // =================================================================
  if (enabled) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-dyndev-green" />
            <CardTitle className="text-lg">Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>Your account is protected with 2FA.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-3 rounded-lg bg-dyndev-green/5 border border-dyndev-green/20 mb-3">
            <p className="text-xs font-medium text-dyndev-green">✅ Two-factor authentication is enabled</p>
          </div>

          {!showDisable ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleViewRecoveryCodes}
                className="gap-1.5"
              >
                View Recovery Codes
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDisable(true)}
                className="gap-1.5 text-red-400 border-red-500/20 hover:bg-red-500/10 hover:text-red-300"
              >
                <ShieldOff size={12} />
                Disable 2FA
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[var(--text-secondary)]">
                Enter your password to disable two-factor authentication.
              </p>
              <Input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                className="text-sm h-8 max-w-xs"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={loading || !disablePassword}
                  onClick={handleDisable}
                  className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
                >
                  {loading && <Loader2 size={12} className="animate-spin" />}
                  Confirm Disable
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Fallback (shouldn't reach here, but TypeScript needs it)
  return null;
}
